(ns hai.knowledge.api
  "Platform API integration for assistant/knowledge features"
  (:require
   [clojure.string :as str]
   [hai.ui.rf-sugar :refer [defevent defsub defevent-fx]]
   [humbleai.belt.map :as belt.map]
   [hai.web-app.platform :as platform]))

(def endpoints
  {:ai-model-configs "/assistant/v1/ai-model-configs"
   :ai-model-config  "/assistant/v1/ai-model-configs/:id"
   :ai-model-config-private-link "/assistant/v1/ai-model-configs/:id/private-link"
   :ai-prompts       "/assistant/v1/ai-prompts/:id"
   :messages         "/assistant/v1/messages"
   :chats            "/assistant/v1/chats"
   :sources          "/assistant/v1/sources"})

(defn endpoint-url
  [endpoint & params]
  (let [base-url (str platform/origin (endpoint endpoints))
        url (reduce (fn [url [k v]]
                      (str/replace url (str k) (str v)))
                    base-url
                    (partition 2 params))]
    url))

(defevent-fx :knowledge/fetch-bases
  [{:keys [db]}]
  {:fetch {:url           (endpoint-url :ai-model-configs)
           :method        :get
           :store-in [:assistant :bases]
           :transform-fn  #(-> % (js->clj :keywordize-keys true))}})

(defevent-fx :knowledge/fetch-base
  [{:keys [db]} base-id request-id]
  {:fetch {:request-id    request-id
           :url           (endpoint-url :ai-model-config :id base-id)
           :method        :get
           :store-in      [:assistant :base-by-id base-id]}})

(defevent :knowledge/clear-base
  [db base-id]
  (belt.map/dissoc-in db [:assistant :base-by-id base-id]))

(defsub :knowledge/active-base [db]
  (get-in db [:assistant :current-base]))

(defsub :knowledge/base [db base-id]
  (get-in db [:assistant :base-by-id base-id]))

(defevent-fx :knowledge/create-base
  [{:keys [db]} [_ base-data]]
  {:fetch {:url           (endpoint-url :ai-model-configs)
           :method        :post
           :body          (merge {:synced-at (.toISOString (js/Date.))}
                                 base-data)
           :content-type  :json
           :accept        :json
           :transform-fn  #(-> % (js->clj :keywordize-keys true))
           :on-success-fx [[:dispatch [:knowledge/base-created]]]}})

(defevent-fx :knowledge/update-base
  [{:keys [db]} base-id {:ai-model-config/keys [title description]
                         :or {title "" description ""}}]
  {:fetch {:request-id    ::knowledge.update-base
           :url           (endpoint-url :ai-model-config :id base-id)
           :method        :put
           :body          {:title title
                           :description description}
           :on-success-fx [[:dispatch [:toast/success {:text "Assistant information updated."}]]]}})

(defevent-fx :knowledge/update-prompt
  [{:keys [db]} base-id {:ai-prompt/keys [uuid title content]}]
  {:fetch {:request-id    ::knowledge.update-prompts
           :url           (endpoint-url :ai-prompts :id uuid)
           :method        :put
           :body          {:title title
                           :content content}
           :on-success-fx [[:dispatch [:toast/success {:text (str "Prompt '" title "' updated.")}]]]}})

(defevent-fx :knowledge/delete-base
  [{:keys [db]} base-id]
  {:fetch {:url           (endpoint-url :ai-model-config :id base-id)
           :method        :delete
           :on-success-fx [[:dispatch [:knowledge/base-deleted base-id]]]}})

(defevent-fx :knowledge/send-message
  [{:keys [db]} chat-id message]
  {:db (assoc-in db [:assistant :streaming?] true)
   :fetch {:url (endpoint-url :messages)
           :method :post
           :body {:chat-id chat-id
                  :content message}
           :content-type :json
           :accept :json
           :transform-fn #(-> % (js->clj :keywordize-keys true))
           :on-success-fx [[:dispatch [:knowledge/message-sent]]]}})

;; Event handlers for API responses
(defevent-fx :knowledge/bases-loaded
  [{:keys [db]} [_ bases]]
  {:db (assoc-in db [:assistant :bases]
                 (zipmap (map :ai-model-config/uuid bases) bases))})

(defevent-fx :knowledge/base-loaded
  [{:keys [db]} [_ base]]
  {:db (assoc-in db [:assistant :current-base] base)
   :fx [[:dispatch [:knowledge/track-recent-assistant
                    (:ai-model-config/uuid base)]]]})

(defevent-fx :knowledge/base-created
  [{:keys [db]} [_ base]]
  {:db (assoc-in db [:assistant :bases (:ai-model-config/uuid base)] base)
   :fx [[:dispatch [:routing/navigate [:tasks/knowledge-config
                                       {:ai-model-config/uuid (:ai-model-config/uuid base)}]]]]})

(defevent-fx :knowledge/base-updated
  [{:keys [db]} base]
  {:db (assoc-in db [:assistant :bases (:ai-model-config/uuid base)] base)})

(defevent-fx :knowledge/base-deleted
  [{:keys [db]} [_ base-id]]
  {:db (update-in db [:assistant :bases] dissoc base-id)
   :fx [[:dispatch [:routing/navigate [:tasks/knowledge]]]]})

;; Sources API events
"Fetch all sources available to the user"
(defevent-fx :knowledge/fetch-sources
  [{:keys [db]} request-id]
  {:fetch {:request-id request-id
           :url (str platform/origin "/assistant/v1/sources")
           :method :get}})

(defevent-fx :knowledge/update-assistant-config
  [{:keys [db]} base-id params]
  {:resolve {:query [(list 'assistant/update (assoc params :ai-model-config/uuid base-id))]
             :on-success-fx [[:dispatch [:entity/resolve {:ai-model-config/uuid base-id}]]]}})

(defevent-fx :knowledge/create-private-link
  [{:keys [db]} base-id]
  {:resolve {:request-id [::create-private-link base-id]
             :query [(list 'ai-model-config/create-private-link {:ai-model-config/uuid base-id})]
             :on-success-fx [[:dispatch [:entity/resolve
                                         {:ai-model-config/uuid base-id}
                                         {:request-id [::create-private-link-resolve base-id]}]]]}})

"Accept a private link token and redirect to the knowledge base"
(defevent-fx :knowledge/accept-private-link
  [{:keys [db]} token]
  {:fetch {:request-id ::knowledge.accept-private-link
           :url (str platform/origin "/assistant/v1/accept-private-link/" token)
           :method :post
           :on-success-fx [[:dispatch [:knowledge/private-link-accepted '?response]]]}})

(defevent-fx :knowledge/private-link-accepted
  [{:keys [db]} {:keys [status body]}]
  (when (= 200 status)
    ;; TODO (evgheni): We should have a route for this, since it's in web-app now.
    ;;                Then we can navigate by route name.
    {:fx [[:routing/navigate-to-url (str platform/origin "/app/chats/" (:chat-uuid body))]]}))

(defsub :assistant/new-assistant
  [db]
  (:assistant/new-assistant db))

(defevent-fx :assistant/create-success
  [{:keys [db]}]
  (let [new-uuid (get-in db [:assistant/new-assistant :uuid])]
    {:fx [[:dispatch [:routing/navigate [:tasks/knowledge-config {:ai-model-config/uuid new-uuid}]]]]}))

(defevent-fx :assistant/create
  [{:keys [db]}]
  {:fetch {:url           (str platform/origin "/assistant/v0/ai-model-configs")
           :method        :post
           :body          {:title     "New assistant"
                           :specify-sources true
                           :synced-at (.toISOString (js/Date.))}
           :store-in      [:assistant/new-assistant]
           :content-type  :json
           :accept        :json
           :transform-fn  #(-> % (js->clj :keywordize-keys true))
           :on-success-fx [[:dispatch [:assistant/create-success]]]}}
  ; This doesn't work ATM due to solution being "required" somehow
  ; and it's also slow
  #_{:fx [[:dispatch [:routing/navigate [:ai-prompts.new/temp-flows
                                         {:workflow/id
                                          "3d7c71e5-9f6d-495f-8eb9-652d86420527"
                                          :block/id
                                          "96a45b51-c475-4050-a1c9-e944856ba6bd"}]]]]})
